import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function QuoteForm() {
  const { t } = useTranslation('inspection');
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    adresse: '',
    motif: '',
    message: '',
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    fetch(form.action, {
      method: form.method,
      body: data,
      headers: { Accept: 'application/json' },
    }).finally(() => {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/merci');
      }
    });
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="quote-form" className="bg-soft-gray py-16 md:py-20">
      <div
        ref={ref}
        className={`max-w-xl mx-auto px-4 md:px-6 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="bg-white rounded-xl p-6 md:p-10">
          <h3 className="font-manrope font-bold text-slate-primary text-xl md:text-2xl text-center mb-2">
            {t('form_h2')}
          </h3>
          <p className="text-gray-500 text-sm text-center mb-8">
            {t('form_subtitle')}
          </p>

          <form
            action="https://readdy.ai/api/form/d84bppm6qilughrrc850"
            method="POST"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="insp-name" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_name_label')}
                </label>
                <input
                  id="insp-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_name_placeholder')}
                />
              </div>
              <div>
                <label htmlFor="insp-phone" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_phone_label')}
                </label>
                <input
                  id="insp-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_phone_placeholder')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="insp-email" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_email_label')}
              </label>
              <input
                id="insp-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                placeholder={t('form_email_placeholder')}
              />
            </div>

            <div>
              <label htmlFor="insp-adresse" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_adresse_label')}
              </label>
              <input
                id="insp-adresse"
                name="adresse"
                type="text"
                value={formData.adresse}
                onChange={(e) => handleChange('adresse', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                placeholder={t('form_adresse_placeholder')}
              />
            </div>

            <div>
              <label htmlFor="insp-motif" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_motif_label')}
              </label>
              <select
                id="insp-motif"
                name="motif"
                value={formData.motif}
                onChange={(e) => handleChange('motif', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
              >
                <option value="">{t('form_motif_select')}</option>
                <option value="preventif">{t('form_motif_preventif')}</option>
                <option value="achat">{t('form_motif_achat')}</option>
                <option value="vente">{t('form_motif_vente')}</option>
                <option value="assurance">{t('form_motif_assurance')}</option>
                <option value="meteo">{t('form_motif_reparation')}</option>
                <option value="autre">{t('form_motif_autre')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="insp-message" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_message_label')}
              </label>
              <textarea
                id="insp-message"
                name="message"
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors resize-none"
                placeholder={t('form_message_placeholder')}
              />
              <p className="text-gray-400 text-xs mt-1 text-right">
                {formData.message.length}{t('form_char_count')}
              </p>
            </div>

            <input
              type="text"
              name="_gotcha"
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <button
              type="submit"
              className="w-full bg-copper hover:bg-copper-dark text-white font-bold py-4 rounded-md text-base transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {t('cta_button')}
              <i className="ri-arrow-right-line" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}